<template>
  <div>
    <slot
      :table-props="tableProps"
      :table-events="tableEvents"
      :filters="filters"
      :update-filter="updateFilter"
      :search-value="searchValue"
      :update-search="updateSearch"
      :search-config="searchConfig"
    />
  </div>
</template>

<script>
/**
 * DataTableUrlSync - A wrapper component that provides URL state synchronization
 * for v-data-table components with filters, sorting, and pagination.
 *
 * Usage:
 * <data-table-url-sync
 *   :filter-config="filterConfig"
 *   :default-sort="{ by: 'created_at', desc: false }"
 *   :default-items-per-page="25"
 * >
 *   <template #default="{ tableProps, tableEvents, filters, updateFilter }">
 *     <v-data-table
 *       v-bind="tableProps"
 *       v-on="tableEvents"
 *       :items="items"
 *       :headers="headers"
 *     />
 *   </template>
 * </data-table-url-sync>
 */
export default {
  name: "data-table-wrapper",
  props: {
    /**
     * Configuration for filters
     * Example: {
     *   status: { default: 'all', urlKey: 'status' },
     *   assignedTo: { default: 'own', urlKey: 'assignedTo' }
     * }
     */
    filterConfig: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Default sort configuration
     * Example: { by: 'created_at', desc: false }
     */
    defaultSort: {
      type: Object,
      default: () => ({ by: null, desc: false }),
    },
    /**
     * Default items per page
     */
    defaultItemsPerPage: {
      type: Number,
      default: 25,
    },
    /**
     * Whether to apply default sort only on first load
     */
    oneTimeDefaultSort: {
      type: Boolean,
      default: true,
    },
    /**
     * Footer props for the data table
     */
    footerProps: {
      type: Object,
      default: () => ({
        "items-per-page-options": [15, 25, 50, 100, -1],
      }),
    },
    /**
     * Enable search functionality with URL sync
     */
    enableSearch: {
      type: Boolean,
      default: false,
    },
    /**
     * Search configuration
     * Example: { urlKey: 'q', placeholder: 'Search...', debounce: 300 }
     */
    searchConfig: {
      type: Object,
      default: () => ({
        urlKey: "search",
        placeholder: "Search...",
        debounce: 100,
      }),
    },
  },
  data() {
    return {
      // Track if user has interacted with sorting (for one-time default)
      hasUserInteractedWithSort: this.$route.query.sortBy !== undefined,
      // Debounce timer for search
      searchDebounceTimer: null,
    };
  },
  computed: {
    /**
     * Reactive filters object based on URL query params
     */
    filters() {
      const filters = {};
      Object.keys(this.filterConfig).forEach((key) => {
        const config = this.filterConfig[key];
        const urlValue = this.$route.query[config.urlKey || key];

        // Check if default is an array (for multi-select filters)
        const isArrayFilter = Array.isArray(config.default);

        // Handle 'none' value - convert back to empty array for array filters
        if (urlValue === "none") {
          filters[key] = isArrayFilter ? [] : "none";
        }
        // Handle array values (for multi-select filters)
        else if (Array.isArray(urlValue)) {
          filters[key] = urlValue;
        } else if (urlValue !== undefined) {
          // If it's a string that looks like an array (comma-separated), parse it
          if (typeof urlValue === "string" && urlValue.includes(",")) {
            filters[key] = urlValue.split(",");
          } else if (isArrayFilter && typeof urlValue === "string") {
            // Single value for an array filter - wrap in array
            filters[key] = [urlValue];
          } else {
            filters[key] = urlValue;
          }
        } else {
          // Only apply default if URL value is truly undefined (not present in URL)
          filters[key] = config.default;
        }
      });
      return filters;
    },

    /**
     * Current search value from URL
     */
    searchValue() {
      if (!this.enableSearch) return "";
      return this.$route.query[this.searchConfig.urlKey] || "";
    },

    /**
     * Table props to bind to v-data-table
     */
    tableProps() {
      return {
        itemsPerPage: this.itemsPerPage,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        multiSort: true,
        footerProps: this.footerProps,
      };
    },

    /**
     * Table events to bind to v-data-table
     */
    tableEvents() {
      return {
        "update:items-per-page": this.onItemsPerPageUpdate,
        "update:sort-by": this.onSortByUpdate,
        "update:sort-desc": this.onSortDescUpdate,
      };
    },

    /**
     * Current items per page from URL or default
     */
    itemsPerPage() {
      return (
        parseInt(this.$route.query.itemsPerPage) || this.defaultItemsPerPage
      );
    },

    /**
     * Current sort by from URL or default
     */
    sortBy() {
      if (this.$route.query.sortBy !== undefined) {
        return this.parseSortBy(this.$route.query.sortBy);
      }

      if (
        this.oneTimeDefaultSort &&
        !this.hasUserInteractedWithSort &&
        this.defaultSort.by
      ) {
        return [this.defaultSort.by];
      }

      return [];
    },

    /**
     * Current sort desc from URL or default
     */
    sortDesc() {
      if (this.$route.query.sortDesc !== undefined) {
        return this.parseSortDesc(this.$route.query.sortDesc);
      }

      if (
        this.oneTimeDefaultSort &&
        !this.hasUserInteractedWithSort &&
        this.defaultSort.by
      ) {
        return [this.defaultSort.desc];
      }

      return [];
    },
  },

  watch: {
    /**
     * Watch route changes for browser navigation
     */
    "$route.query": {
      handler(newQuery) {
        // Update sort interaction tracking
        if (newQuery.sortBy !== undefined) {
          this.hasUserInteractedWithSort = true;
        }

        // Emit filter changes
        this.emitFilterChanges();
      },
      deep: true,
    },
  },

  mounted() {
    const urlUpdates = {};

    // Apply default sort to URL if no sort parameters are present
    if (
      this.defaultSort.by &&
      this.$route.query.sortBy === undefined &&
      this.$route.query.sortDesc === undefined
    ) {
      urlUpdates.sortBy = this.defaultSort.by;
      urlUpdates.sortDesc = String(this.defaultSort.desc);
    }

    // Apply default filters to URL if no filter parameters are present
    Object.keys(this.filterConfig).forEach((key) => {
      const config = this.filterConfig[key];
      const urlKey = config.urlKey || key;

      if (this.$route.query[urlKey] === undefined) {
        urlUpdates[urlKey] = config.default;
      }
    });

    // Update URL with any missing defaults
    if (Object.keys(urlUpdates).length > 0) {
      this.updateRouteQuery(urlUpdates);
    }

    // Emit initial filter state
    this.emitFilterChanges();
  },

  methods: {
    /**
     * Update a specific filter
     */
    updateFilter(filterKey, value) {
      const config = this.filterConfig[filterKey];
      if (!config) {
        console.warn(`Filter key "${filterKey}" not found in filterConfig`);
        return;
      }

      let urlValue = value;

      // Convert null to 'none' to explicitly indicate no value (e.g., no grouping)
      if (value === null) {
        urlValue = "none";
      }
      // Handle object values with a 'value' property (like groupBy selector)
      else if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value) &&
        value.value !== undefined
      ) {
        // If the object's value property is null, convert to 'none'
        urlValue = value.value === null ? "none" : value.value || undefined;
      }
      // Handle array values by joining them with commas
      else if (Array.isArray(value)) {
        urlValue = value.length > 0 ? value.join(",") : "none";
      }

      // Handle empty values, but preserve explicit 'none' string
      if ((value === undefined || value === "") && urlValue !== "none") {
        urlValue = undefined;
      }

      this.updateRouteQuery({
        [config.urlKey || filterKey]: urlValue,
      });
    },

    /**
     * Update search value with debouncing
     */
    updateSearch(value) {
      if (!this.enableSearch) return;

      // Clear existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      // Set up debounced update
      this.searchDebounceTimer = setTimeout(() => {
        this.updateRouteQuery({
          [this.searchConfig.urlKey]: value || undefined, // undefined removes the param
        });
      }, this.searchConfig.debounce);
    },

    /**
     * Parse sort by query parameter
     */
    parseSortBy(sortByQuery) {
      if (!sortByQuery) return [];
      if (typeof sortByQuery === "string") {
        return sortByQuery.split(",");
      }
      return Array.isArray(sortByQuery) ? sortByQuery : [sortByQuery];
    },

    /**
     * Parse sort desc query parameter
     */
    parseSortDesc(sortDescQuery) {
      if (!sortDescQuery) return [];
      if (typeof sortDescQuery === "string") {
        return sortDescQuery.split(",").map((val) => val === "true");
      }
      return Array.isArray(sortDescQuery)
        ? sortDescQuery.map((val) => val === "true" || val === true)
        : [sortDescQuery === "true" || sortDescQuery === true];
    },

    /**
     * Serialize sort by array for URL
     */
    serializeSortBy(sortByArray) {
      return Array.isArray(sortByArray) && sortByArray.length > 0
        ? sortByArray.join(",")
        : null;
    },

    /**
     * Serialize sort desc array for URL
     */
    serializeSortDesc(sortDescArray) {
      return Array.isArray(sortDescArray) && sortDescArray.length > 0
        ? sortDescArray.join(",")
        : null;
    },

    /**
     * Check if current sort is the default
     */
    isDefaultSort(sortBy, sortDesc) {
      if (!this.defaultSort.by) return false;
      return (
        JSON.stringify(sortBy) === JSON.stringify([this.defaultSort.by]) &&
        JSON.stringify(sortDesc) === JSON.stringify([this.defaultSort.desc])
      );
    },

    /**
     * Sync sort arrays to ensure consistent lengths
     */
    syncSortArrays(sortBy, sortDesc) {
      if (!sortBy) sortBy = [];
      if (!sortDesc) sortDesc = [];

      // Pad sortDesc with false values if shorter
      while (sortDesc.length < sortBy.length) {
        sortDesc.push(false);
      }

      // Trim sortDesc if longer
      if (sortDesc.length > sortBy.length) {
        sortDesc = sortDesc.slice(0, sortBy.length);
      }

      return { sortBy, sortDesc };
    },

    /**
     * Update route query parameters
     */
    updateRouteQuery(newQuery) {
      const query = { ...this.$route.query, ...newQuery };

      // Clean up default values
      this.cleanDefaultValues(query);

      // Only update if query has changed
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router
          .replace({
            name: this.$route.name,
            params: this.$route.params,
            query,
          })
          .catch((err) => {
            if (err.name !== "NavigationDuplicated") {
              console.warn("Router navigation error:", err);
            }
          });
      }
    },

    /**
     * Clean default values from query to keep URL clean
     */
    cleanDefaultValues(query) {
      // Note: We no longer clean filter defaults or sort defaults
      // as we want URL to always reflect the actual state

      // Clean search if empty
      if (
        this.enableSearch &&
        (!query[this.searchConfig.urlKey] ||
          query[this.searchConfig.urlKey] === "")
      ) {
        delete query[this.searchConfig.urlKey];
      }

      // Clean pagination default
      if (query.itemsPerPage === String(this.defaultItemsPerPage)) {
        delete query.itemsPerPage;
      }

      // Clean sort defaults - only remove if empty, not if default
      if (!query.sortBy || query.sortBy === "") {
        delete query.sortBy;
        delete query.sortDesc;
      }
    },

    /**
     * Handle items per page update
     */
    onItemsPerPageUpdate(itemsPerPage) {
      this.updateRouteQuery({ itemsPerPage });
    },

    /**
     * Handle sort by update
     */
    onSortByUpdate(sortBy) {
      this.hasUserInteractedWithSort = true;

      const synced = this.syncSortArrays(sortBy, this.sortDesc);

      this.updateRouteQuery({
        sortBy: this.serializeSortBy(synced.sortBy),
        sortDesc: this.serializeSortDesc(synced.sortDesc),
      });
    },

    /**
     * Handle sort desc update
     */
    onSortDescUpdate(sortDesc) {
      this.hasUserInteractedWithSort = true;

      const synced = this.syncSortArrays(this.sortBy, sortDesc);

      this.updateRouteQuery({
        sortBy: this.serializeSortBy(synced.sortBy),
        sortDesc: this.serializeSortDesc(synced.sortDesc),
      });
    },

    /**
     * Emit filter changes to parent
     */
    emitFilterChanges() {
      this.$emit("filters-changed", this.filters);
    },
  },
};
</script>
